import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Users, UserPlus, FileWarning, CreditCard, CalendarClock, AlertTriangle,
  ListChecks, Megaphone, Image as ImageIcon, Plus, Check, Clock, Send,
  CalendarPlus, Bell, X, Loader2, ChevronRight,
} from "lucide-react";

const CAT = {
  rentree: "Rentrée", evenement: "Événement", communication: "Communication",
  financement: "Financement", eleve: "Élève", parent: "Parent", equipe: "Équipe",
  equipement: "Équipement", paiement: "Paiement", marketing: "Marketing",
};
const PRIO = {
  urgente: { l: "Urgente", c: "bg-red-500/20 text-red-300 border-red-500/40" },
  haute: { l: "Haute", c: "bg-orange-500/20 text-orange-300 border-orange-500/40" },
  moyenne: { l: "Normale", c: "bg-sky-500/20 text-sky-300 border-sky-500/40" },
  basse: { l: "Basse", c: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40" },
};
const STATUT = { a_faire: "À faire", en_cours: "En cours", en_attente: "En attente", fait: "Terminé" };

const money = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n || 0);

export default function AccueilPilotageModule() {
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("semaine"); // aujourdhui / semaine / retard / tous
  const [fCat, setFCat] = useState("");
  const [showTask, setShowTask] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, t] = await Promise.all([
        api.get("/accueil-pilotage/overview"),
        api.get("/tasks/overview"),
      ]);
      setOv(o.data); setTasks(t.data.tasks || []);
    } catch (e) { toast.error("Chargement du tableau de bord échoué."); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const inWeek = (d) => { if (!d) return false; const x = new Date(d); const diff = (x - now) / 86400000; return diff <= 7; };
  const isToday = (d) => d && new Date(d).toDateString() === now.toDateString();
  const isLate = (t) => t.statut !== "fait" && t.due_date && new Date(t.due_date) < now;

  const filtered = tasks.filter((t) => {
    if (fCat && t.categorie !== fCat) return false;
    if (view === "aujourdhui") return isToday(t.due_date) && t.statut !== "fait";
    if (view === "semaine") return t.statut !== "fait" && (!t.due_date || inWeek(t.due_date));
    if (view === "retard") return isLate(t);
    return true;
  });

  const toggleDone = async (t) => {
    try { await api.put(`/tasks/${t.id}`, { statut: t.statut === "fait" ? "a_faire" : "fait" }); load(); }
    catch { toast.error("Erreur"); }
  };

  if (loading || !ov) return (
    <div className="min-h-[60vh] flex items-center justify-center text-zinc-400"><Loader2 className="animate-spin mr-2" /> Chargement…</div>
  );

  const i = ov.indicators;
  const cards = [
    { k: "membres_actifs", label: "Membres actifs", icon: Users, tone: "text-emerald-400", to: "/app/audit-membres" },
    { k: "familles", label: "Familles à joindre", icon: Users, tone: "text-sky-400" },
    { k: "activation_manquante", label: "Activations manquantes", icon: UserPlus, tone: "text-amber-400", to: "/app/conformite" },
    { k: "documents_manquants", label: "Documents manquants", icon: FileWarning, tone: "text-amber-400", to: "/app/conformite" },
    { k: "paiement_a_verifier", label: "Paiements à vérifier", icon: CreditCard, tone: "text-red-400", to: "/app/conformite" },
    { k: "tarif_non_confirme", label: "Tarifs non confirmés", icon: CreditCard, tone: "text-orange-400", to: "/app/conformite" },
    { k: "evenements_a_venir", label: "Événements à venir", icon: CalendarClock, tone: "text-violet-400", to: "/app/calendrier" },
    { k: "taches_urgentes", label: "Tâches urgentes", icon: AlertTriangle, tone: "text-red-400" },
    { k: "communications_attente", label: "Communications à approuver", icon: Megaphone, tone: "text-sky-400" },
    { k: "affiches_a_produire", label: "Affiches à produire", icon: ImageIcon, tone: "text-pink-400" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="accueil-pilotage-page">
      {/* A. Bandeau d'accueil */}
      <div className="rounded-3xl border border-[#C9A961]/30 bg-gradient-to-br from-zinc-900 via-zinc-900 to-[#1a1408] p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[#C9A961] text-xs uppercase tracking-widest mb-1">Institut Impact Élite Martial · Val-d'Or</div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Accueil & Pilotage 🎛️</h1>
            <p className="text-zinc-400 text-sm mt-1 capitalize">{now.toLocaleDateString("fr-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
          </div>
          {ov.next_event && (
            <div className="rounded-2xl bg-black/40 border border-white/10 px-5 py-3 text-right" data-testid="pilotage-countdown">
              <div className="text-[11px] text-zinc-500 uppercase tracking-wide">Prochain grand événement</div>
              <div className="text-white font-semibold">{ov.next_event.title}</div>
              <div className="text-[#C9A961] text-2xl font-extrabold">J−{ov.next_event.days}</div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <QuickBtn onClick={() => nav("/app/calendrier-annuel")} icon={CalendarPlus} label="Créer un événement" testid="qa-event" />
          <QuickBtn onClick={() => nav("/app/centre-suivi")} icon={Send} label="Envoyer une communication" testid="qa-comm" />
          <QuickBtn onClick={() => nav("/app/generateur-affiches")} icon={ImageIcon} label="Générer une affiche" testid="qa-poster" />
          <QuickBtn onClick={() => setShowTask(true)} icon={Plus} label="Ajouter une tâche" testid="qa-task" />
          <QuickBtn onClick={() => nav("/app/calendrier-annuel")} icon={CalendarClock} label="Calendrier annuel" testid="qa-cal" />
          <QuickBtn onClick={() => setMeetingOpen(true)} icon={Users} label="Réunion de rentrée" primary testid="qa-meeting" />
        </div>
      </div>

      {/* B. Indicateurs rapides */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pilotage-indicators">
        {cards.map((c) => (
          <button key={c.k} onClick={() => c.to && nav(c.to)} data-testid={`kpi-${c.k}`}
            className={`text-left rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition ${c.to ? "hover:border-[#C9A961]/40 hover:bg-white/[0.06]" : "cursor-default"}`}>
            <c.icon className={`w-5 h-5 ${c.tone} mb-2`} />
            <div className="text-2xl font-extrabold text-white">{i[c.k]}</div>
            <div className="text-[11px] text-zinc-400 leading-tight mt-0.5">{c.label}</div>
          </button>
        ))}
        <button onClick={() => nav("/app/financement")} data-testid="kpi-financement" className="text-left col-span-2 rounded-2xl border border-emerald-700/40 bg-emerald-950/20 p-4 hover:border-emerald-500/60 hover:bg-emerald-950/30 transition">
          <div className="text-[11px] text-emerald-300 uppercase tracking-wide">Financement</div>
          <div className="text-xl font-extrabold text-white">{money(i.financement_recolte)} <span className="text-sm text-zinc-500 font-normal">/ {money(i.financement_objectif)}</span></div>
          <div className="mt-2 h-2 rounded-full bg-black/40 overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${i.financement_objectif ? Math.min(100, i.financement_recolte * 100 / i.financement_objectif) : 0}%` }} />
          </div>
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* C. Priorités de la semaine */}
        <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/[0.02] p-5" data-testid="pilotage-priorities">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h2 className="text-white font-semibold flex items-center gap-2"><ListChecks className="w-5 h-5 text-[#C9A961]" /> Priorités</h2>
            <div className="flex items-center gap-1.5 flex-wrap">
              {[["aujourdhui", "Aujourd'hui"], ["semaine", "Cette semaine"], ["retard", "En retard"], ["tous", "Tout"]].map(([v, l]) => (
                <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full ${view === v ? "bg-[#C9A961] text-black font-semibold" : "bg-white/5 text-zinc-400 hover:text-white"}`}>{l}</button>
              ))}
              <button onClick={() => setShowTask(true)} data-testid="add-task-btn" className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-600 text-white font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Tâche</button>
            </div>
          </div>
          <select value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="filter-cat"
            className="mb-3 bg-zinc-900 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-300">
            <option value="">Toutes les catégories</option>
            {Object.entries(CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {filtered.length === 0 ? (
            <p className="text-zinc-500 text-sm py-6 text-center">Aucune tâche pour cette vue. 🎉</p>
          ) : (
            <div className="space-y-2" data-testid="task-list">
              {filtered.map((t) => (
                <div key={t.id} className="flex items-start gap-3 rounded-xl bg-white/[0.03] border border-white/5 p-3" data-testid={`task-${t.id}`}>
                  <button onClick={() => toggleDone(t)} data-testid={`task-done-${t.id}`}
                    className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${t.statut === "fait" ? "bg-emerald-600 border-emerald-600" : "border-white/30 hover:border-[#C9A961]"}`}>
                    {t.statut === "fait" && <Check className="w-3.5 h-3.5 text-white" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm ${t.statut === "fait" ? "line-through text-zinc-500" : "text-zinc-100"}`}>{t.titre}</div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${(PRIO[t.priorite] || PRIO.moyenne).c}`}>{(PRIO[t.priorite] || PRIO.moyenne).l}</span>
                      {t.categorie && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400">{CAT[t.categorie] || t.categorie}</span>}
                      {t.responsable && <span className="text-[10px] text-zinc-500">👤 {t.responsable}</span>}
                      {t.due_date && <span className={`text-[10px] ${isLate(t) ? "text-red-400" : "text-zinc-500"}`}>📅 {String(t.due_date).slice(0, 10)}</span>}
                      {t.lien && <button onClick={() => nav(t.lien)} className="text-[10px] text-sky-400 hover:underline">Ouvrir →</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* D. Alertes importantes */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-5" data-testid="pilotage-alerts">
          <h2 className="text-white font-semibold flex items-center gap-2 mb-3"><Bell className="w-5 h-5 text-red-400" /> Alertes importantes</h2>
          {ov.alerts.length === 0 ? (
            <p className="text-zinc-500 text-sm py-6 text-center">Tout est en ordre. ✅</p>
          ) : (
            <div className="space-y-2">
              {ov.alerts.map((a, idx) => (
                <button key={idx} onClick={() => a.lien && nav(a.lien)} data-testid={`alert-${idx}`}
                  className={`w-full text-left rounded-xl border p-3 flex items-start gap-2 transition hover:opacity-90 ${a.level === "haute" ? "border-red-500/40 bg-red-500/10" : "border-amber-500/40 bg-amber-500/10"}`}>
                  <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${a.level === "haute" ? "text-red-400" : "text-amber-400"}`} />
                  <span className="text-xs text-zinc-200 flex-1">{a.texte}</span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showTask && <TaskModal onClose={() => setShowTask(false)} reload={load} />}
      {meetingOpen && <MeetingManager onClose={() => setMeetingOpen(false)} />}
    </div>
  );
}

function QuickBtn({ onClick, icon: Icon, label, primary, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${primary ? "bg-[#C9A961] text-black hover:bg-white" : "bg-white/5 text-white hover:bg-white/10 border border-white/10"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function TaskModal({ onClose, reload }) {
  const [f, setF] = useState({ titre: "", categorie: "rentree", priorite: "moyenne", responsable: "", due_date: "", lien: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.titre.trim()) { toast.error("Titre requis"); return; }
    setBusy(true);
    try { await api.post("/tasks", f); toast.success("Tâche ajoutée."); reload(); onClose(); }
    catch { toast.error("Erreur"); } setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="task-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-900 p-6 space-y-3">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-white">Nouvelle tâche</h3><button onClick={onClose}><X className="w-5 h-5 text-zinc-500" /></button></div>
        <input autoFocus value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })} placeholder="Titre de la tâche" data-testid="task-titre"
          className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value })} data-testid="task-cat" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-zinc-200">
            {Object.entries(CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={f.priorite} onChange={(e) => setF({ ...f, priorite: e.target.value })} data-testid="task-prio" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-zinc-200">
            <option value="haute">Haute</option><option value="moyenne">Normale</option><option value="basse">Basse</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input value={f.responsable} onChange={(e) => setF({ ...f, responsable: e.target.value })} placeholder="Responsable" data-testid="task-resp" className="bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
          <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due" className="bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200" />
        </div>
        <button onClick={save} disabled={busy} data-testid="task-save" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-2 rounded-lg text-sm disabled:opacity-50">{busy ? "…" : "Ajouter la tâche"}</button>
      </div>
    </div>
  );
}

// Réunion obligatoire de rentrée
function MeetingManager({ onClose }) {
  const [meetings, setMeetings] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/accueil-pilotage/meetings"); setMeetings(data.meetings || []); }
    catch { toast.error("Erreur chargement réunions"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/accueil-pilotage/meetings", { date: "", time: "", target: "tous" });
      toast.success("Réunion créée (ordre du jour pré-rempli).");
      await load(); setSel(data.meeting);
    } catch { toast.error("Erreur"); } setBusy(false);
  };
  const openMeeting = async (m) => {
    const { data } = await api.get(`/accueil-pilotage/meetings/${m.id}`); setSel(data.meeting); setMsg(null);
  };
  const save = async (patch) => {
    setBusy(true);
    try {
      const body = { ...sel, ...patch };
      const { data } = await api.put(`/accueil-pilotage/meetings/${sel.id}`, body);
      setSel(data.meeting); await load(); toast.success("Enregistré.");
    } catch { toast.error("Erreur"); } setBusy(false);
  };
  const populate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/accueil-pilotage/meetings/${sel.id}/populate`); toast.success(`${data.added} membre(s) ajouté(s).`); await openMeeting(sel); }
    catch { toast.error("Erreur"); } setBusy(false);
  };
  const genMsg = async () => {
    try { const { data } = await api.get(`/accueil-pilotage/meetings/${sel.id}/invite-message`); setMsg(data); }
    catch { toast.error("Erreur"); }
  };
  const sendReminders = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/accueil-pilotage/meetings/${sel.id}/send-reminders`); toast.success(`Rappels : ${data.email_sent} courriels, ${data.sms_sent} SMS.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } setBusy(false);
  };
  const setInv = async (iid, status) => {
    await api.put(`/accueil-pilotage/meetings/${sel.id}/invitees/${iid}`, { status });
    await openMeeting(sel);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="meeting-manager">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-white flex items-center gap-2"><Users className="w-5 h-5 text-[#C9A961]" /> Réunion obligatoire de rentrée</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-500" /></button>
        </div>

        {!sel ? (
          <>
            <button onClick={create} disabled={busy} data-testid="meeting-create"
              className="w-full bg-[#C9A961] hover:bg-white text-black font-semibold py-2.5 rounded-xl text-sm flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Créer la « Rencontre obligatoire — Début de la nouvelle session »
            </button>
            <div className="space-y-2" data-testid="meeting-list">
              {meetings.map((m) => (
                <button key={m.id} onClick={() => openMeeting(m)} data-testid={`meeting-row-${m.id}`}
                  className="w-full text-left rounded-xl border border-white/10 bg-white/[0.03] p-3 hover:bg-white/[0.06]">
                  <div className="text-white text-sm font-medium">{m.title}</div>
                  <div className="text-xs text-zinc-500">{m.date || "date à définir"} · {m.stats.confirmed} confirmés / {m.stats.total} invités</div>
                </button>
              ))}
              {meetings.length === 0 && <p className="text-zinc-500 text-sm text-center py-4">Aucune réunion. Crée la première ci-dessus.</p>}
            </div>
          </>
        ) : (
          <div className="space-y-4" data-testid="meeting-detail">
            <button onClick={() => setSel(null)} className="text-xs text-zinc-400 hover:text-white">← Retour</button>
            <div className="grid grid-cols-2 gap-2">
              <input defaultValue={sel.date} onBlur={(e) => save({ date: e.target.value })} type="date" data-testid="meeting-date" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-zinc-200" />
              <input defaultValue={sel.time} onBlur={(e) => save({ time: e.target.value })} placeholder="Heure (ex. 18h30)" data-testid="meeting-time" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-white" />
              <input defaultValue={sel.location} onBlur={(e) => save({ location: e.target.value })} placeholder="Lieu" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-white col-span-2" />
              <input defaultValue={sel.meeting_link} onBlur={(e) => save({ meeting_link: e.target.value })} placeholder="Lien Zoom / Meet (optionnel)" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-sm text-white col-span-2" />
            </div>

            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
              <div className="text-xs text-zinc-400 uppercase tracking-wide mb-2">Ordre du jour (15 points)</div>
              <ol className="list-decimal list-inside text-xs text-zinc-300 space-y-0.5 max-h-40 overflow-y-auto">
                {(sel.agenda || []).map((a, idx) => <li key={idx}>{a}</li>)}
              </ol>
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={populate} disabled={busy} data-testid="meeting-populate" className="text-xs px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-semibold">➕ Ajouter tous les membres actifs</button>
              <button onClick={genMsg} data-testid="meeting-genmsg" className="text-xs px-3 py-2 rounded-lg bg-[#C9A961] hover:bg-white text-black font-semibold">✉️ Générer le message d'invitation</button>
              <button onClick={sendReminders} disabled={busy} data-testid="meeting-remind" className="text-xs px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"><Bell className="w-3 h-3 inline" /> Rappel aux non-répondants</button>
            </div>

            {msg && (
              <div className="rounded-xl bg-zinc-950 border border-white/10 p-3" data-testid="meeting-invite-msg">
                <div className="text-xs text-zinc-500 mb-1">Objet : {msg.subject}</div>
                <textarea readOnly value={msg.body} rows={8} className="w-full bg-transparent text-xs text-zinc-200 whitespace-pre-line" />
                <button onClick={() => { navigator.clipboard.writeText(msg.body); toast.success("Message copié !"); }} className="text-xs px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white">Copier le message</button>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-zinc-400 uppercase tracking-wide">Confirmations ({sel.stats.confirmed}✓ · {sel.stats.declined}✗ · {sel.stats.pending} en attente)</div>
              </div>
              <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="meeting-invitees">
                {(sel.invitees || []).map((iv) => (
                  <div key={iv.iid} className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.03] px-3 py-1.5">
                    <div className="min-w-0"><div className="text-sm text-zinc-100 truncate">{iv.name || iv.email}</div><div className="text-[10px] text-zinc-500 truncate">{iv.email}</div></div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setInv(iv.iid, "confirme")} data-testid={`inv-confirm-${iv.iid}`} className={`text-[10px] px-2 py-1 rounded ${iv.status === "confirme" ? "bg-emerald-600 text-white" : "bg-white/5 text-zinc-400 hover:text-white"}`}>✓</button>
                      <button onClick={() => setInv(iv.iid, "refuse")} data-testid={`inv-decline-${iv.iid}`} className={`text-[10px] px-2 py-1 rounded ${iv.status === "refuse" ? "bg-red-600 text-white" : "bg-white/5 text-zinc-400 hover:text-white"}`}>✗</button>
                    </div>
                  </div>
                ))}
                {(sel.invitees || []).length === 0 && <p className="text-zinc-500 text-xs text-center py-3">Aucun invité. Utilise « Ajouter tous les membres actifs ».</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
